import type {
  CompositionV3Corrections,
  CompositionV3Filters,
} from '@revelio/data-access';

import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
  type StaticListFilter,
} from '../../types/filters';
import { getNumericIdFromNestedId } from '../get-numeric-id-from-nested-id';
import { getDateFromFilters } from '../getDateFilter';
import { getListFilter } from '../getListFilter';
import { getStaticListBoolean } from '../getStaticListBoolean';
import { getV2CompanyFilters } from '../getV2CompanyFilters';
import { V2DateGranularity } from '../getV2Filters';
import { getV2GeographyFilters } from '../getV2GeographyFilters';
import {
  appendRoleSelectionToFilters,
  appendSkillSelectionToFilters,
} from './role-skill-ent-kind';

function processFilterSelection(
  acc: CompositionV3Filters,
  filter: Filter
): CompositionV3Filters {
  const getV2ListFilter = (key: keyof CompositionV3Filters) => {
    if (filter.type !== FilterType.LIST) return acc;
    return {
      ...acc,
      ...getListFilter<CompositionV3Filters>(filter, key),
    };
  };

  switch (filter.id) {
    case FilterId.COMPANY:
      return { ...acc, ...getV2CompanyFilters(filter) };

    case FilterId.INDUSTRY:
      return { ...acc, ...getV2CompanyFilters(filter) };

    case FilterId.GEOGRAPHY:
      return { ...acc, ...getV2GeographyFilters(filter) };

    case FilterId.ROLE: {
      if (filter.type !== FilterType.TREE) return acc;
      return filter.selections.reduce<CompositionV3Filters>(
        (inner, selection) => {
          const id = getNumericIdFromNestedId(String(selection.id));
          if (id === null) return inner;
          return {
            ...inner,
            ...appendRoleSelectionToFilters(
              inner,
              String(selection.levelId),
              id
            ),
          };
        },
        acc
      );
    }

    case FilterId.CUSTOM_SKILL: {
      if (filter.type !== FilterType.TREE) return acc;
      return filter.selections.reduce<CompositionV3Filters>(
        (inner, selection) => {
          const id = getNumericIdFromNestedId(String(selection.id));
          if (id === null) return inner;
          return {
            ...inner,
            ...appendSkillSelectionToFilters(
              inner,
              String(selection.levelId),
              id
            ),
          };
        },
        acc
      );
    }

    case FilterId.GENDER:
      return getV2ListFilter('gender');

    case FilterId.ETHNICITY:
      return getV2ListFilter('ethnicity');

    case FilterId.EDUCATION:
      return getV2ListFilter('highest_degree');

    case FilterId.SENIORITY:
      return getV2ListFilter('seniority');

    case FilterId.TITLE: {
      if (filter.type === FilterType.FREE_TEXT) {
        return { ...acc, raw_title: [filter.text] };
      }
      return acc;
    }

    case FilterId.KEYWORDS: {
      if (filter.type === FilterType.FREE_TEXT) {
        return { ...acc, keywords: [filter.text] };
      }
      return acc;
    }

    default:
      return acc;
  }
}

export const getCompositionV3Filters = ({
  primaryDimension,
  isSnapshot = false,
  effectiveFilters,
}: {
  primaryDimension: PrimaryFilterId;
  isSnapshot?: boolean;
  effectiveFilters: Filter[];
}): CompositionV3Filters => {
  const filters = effectiveFilters.reduce<CompositionV3Filters>(
    (acc, filter) => {
      if (filter.id === primaryDimension) {
        return acc;
      }
      return processFilterSelection(acc, filter);
    },
    {}
  );

  const dateFilter = getDateFromFilters({
    filters: effectiveFilters,
    granularity: V2DateGranularity,
    isSnapshot,
  });

  if (dateFilter === null) {
    return filters;
  }

  return {
    ...filters,
    start_date: dateFilter.start_date,
    end_date: dateFilter.end_date,
  };
};

function mapLegacyRawToV3WeightFlags(
  raw: boolean | null | undefined
): Pick<CompositionV3Corrections, 'with_sample_weight' | 'with_weight'> {
  return {
    with_sample_weight: true,
    with_weight: raw !== true,
  };
}

const findStaticListFilter = (
  effectiveFilters: Filter[],
  activeFilterMap: ReadonlyMap<FilterId, Filter> | undefined,
  filterId: FilterId
): StaticListFilter | undefined => {
  const fromEffective = effectiveFilters.find(
    (f): f is StaticListFilter =>
      f.id === filterId && f.type === FilterType.STATIC_LIST
  );

  if (fromEffective !== undefined && fromEffective.selections.length > 0) {
    return fromEffective;
  }
  const fromMap = activeFilterMap?.get(filterId);
  if (fromMap?.type === FilterType.STATIC_LIST) {
    return fromMap;
  }
  return fromEffective;
};

export const getCompositionV3Corrections = (
  effectiveFilters: Filter[],
  activeFilterMap?: ReadonlyMap<FilterId, Filter>
): CompositionV3Corrections => {
  const dataAdjFilter = findStaticListFilter(
    effectiveFilters,
    activeFilterMap,
    FilterId.DATA_ADJUSTMENTS
  );
  const rawFromPreference = (() => {
    if (dataAdjFilter === undefined) {
      return undefined;
    }
    const adj = getStaticListBoolean(dataAdjFilter, 'raw');
    if (!('raw' in adj)) {
      return undefined;
    }
    return adj.raw;
  })();

  const contingentFilter = findStaticListFilter(
    effectiveFilters,
    activeFilterMap,
    FilterId.CONTINGENT_WORKERS
  );
  const withContingentWorkers = (() => {
    if (contingentFilter === undefined) {
      return false;
    }
    const b = getStaticListBoolean(contingentFilter, 'with_contingent_workers');
    if (!('with_contingent_workers' in b)) {
      return false;
    }
    return b.with_contingent_workers;
  })();

  return {
    ...mapLegacyRawToV3WeightFlags(rawFromPreference),
    with_contingent_workers: withContingentWorkers,
  };
};
